import { ApiError } from '../lib/api'
import type { EncounterView } from './clinical'

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : 'http://127.0.0.1:8000/api/v1')

/**
 * AI-Scribe API client — the provider "Speak encounter" dock.
 *
 * Pinned contract (Dex implements the backend to exactly this):
 *
 * - POST /api/v1/patients/{patient_id}/scribe/audio
 *     multipart: audio file (field "audio") + encounter fields
 *     (type, facility_id, period_start, practitioner_id?, reason_text?)
 *     → 200 { scribe_id, transcript,
 *             structured_note: { sections: {...}, sections_present: [...] },
 *             suggestions: [{ system, code, name, score }] }
 *
 * - POST /api/v1/patients/{patient_id}/scribe/commit
 *     JSON { scribe_id, encounter fields (editable), note_body (provider-edited
 *     final text) } → 201 EncounterView (the real encounter, source=clinician,
 *     FHIR view — the same shape as the encounters router).
 *
 * Failures surface as thrown ApiError so the dock renders honest retryable
 * error states — no fabricated transcript/note content anywhere.
 */

export type ScribeSuggestion = {
  system: string
  code: string
  name: string
  score: number
}

export type ScribeStructuredNote = {
  sections: Record<string, string>
  sections_present: string[]
}

export type ScribeAudioResponse = {
  scribe_id: string
  transcript: string
  structured_note: ScribeStructuredNote
  suggestions: ScribeSuggestion[]
}

export type ScribeEncounterFields = {
  type: string
  facility_id: string
  period_start: string
  practitioner_id?: string
  reason_text?: string
}

export type ScribeCommitPayload = ScribeEncounterFields & {
  scribe_id: string
  note_body: string
}

async function parseError(response: Response): Promise<never> {
  let detail: unknown = response.statusText
  try {
    const body = await response.json()
    detail = body.detail ?? body
  } catch {
    /* non-JSON error body */
  }
  throw new ApiError(response.status, detail)
}

/**
 * Upload the recorded audio blob + encounter fields as multipart form data.
 * The browser sets the multipart boundary; do NOT set content-type manually.
 */
export async function postScribeAudio(
  patientId: string,
  audio: Blob,
  fields: ScribeEncounterFields,
  options: { filename?: string; signal?: AbortSignal } = {},
): Promise<ScribeAudioResponse> {
  const form = new FormData()
  form.append('audio', audio, options.filename ?? 'encounter-audio.webm')
  form.append('type', fields.type)
  form.append('facility_id', fields.facility_id)
  form.append('period_start', fields.period_start)
  if (fields.practitioner_id) form.append('practitioner_id', fields.practitioner_id)
  if (fields.reason_text) form.append('reason_text', fields.reason_text)

  const response = await fetch(`${API_BASE}/patients/${patientId}/scribe/audio`, {
    method: 'POST',
    body: form,
    signal: options.signal,
  })
  if (!response.ok) return parseError(response)
  return (await response.json()) as ScribeAudioResponse
}

/** Commit the provider-edited note as a real encounter → EncounterView. */
export async function postScribeCommit(
  patientId: string,
  payload: ScribeCommitPayload,
  options: { signal?: AbortSignal } = {},
): Promise<EncounterView> {
  const response = await fetch(`${API_BASE}/patients/${patientId}/scribe/commit`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options.signal,
  })
  if (!response.ok) return parseError(response)
  return (await response.json()) as EncounterView
}
